import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Loader2, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { todayISO, formatBRL } from "@/lib/api";
import confetti from "canvas-confetti";

export default function CheckinDialog({ open, onOpenChange, task, onDone }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef();
    const photoRequired = !!task?.photo_required;

    useEffect(() => {
        if (!open) {
            setFile(null);
            setPreview(null);
        }
    }, [open]);

    const choose = (f) => {
        if (!f) return;
        setFile(f);
        const r = new FileReader();
        r.onload = (e) => setPreview(e.target.result);
        r.readAsDataURL(f);
    };

    const celebrate = () => {
        const colors = ["#D96B43", "#E5A93C", "#4A7C59", "#3A86FF"];
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 }, colors });
        setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors }), 200);
        setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors }), 400);
    };

    const submit = async () => {
        if (!task) return;
        if (photoRequired && !file) {
            toast.error("Essa tarefa precisa de foto!");
            return;
        }
        setBusy(true);
        try {
            let photo_path = null;
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                photo_path = data.path;
            }
            await api.post("/checkins", {
                task_id: task.id,
                date: todayISO(),
                photo_path,
            });
            celebrate();
            toast.success(
                task.reward_amount > 0
                    ? `Mandou bem! +${formatBRL(task.reward_amount)}`
                    : "Tarefa concluída!"
            );
            onDone?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erro ao registrar check-in");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="checkin-dialog">
                <DialogHeader>
                    <DialogTitle>Check-in: {task?.title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {task?.reward_amount > 0 && (
                        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm">
                            <span className="text-amber-900 font-semibold">Recompensa:</span>{" "}
                            <span className="text-amber-800">{formatBRL(task.reward_amount)}</span>
                        </div>
                    )}
                    {photoRequired && (
                        <div className="rounded-xl bg-[#FFF1EA] border border-[#F2CBB6] px-4 py-2.5 text-sm flex items-center gap-2 text-[#8f3b1e]">
                            <AlertCircle className="h-4 w-4 shrink-0" />
                            Essa tarefa pede uma foto para concluir.
                        </div>
                    )}
                    <div className="flex flex-col items-center gap-3">
                        <input
                            type="file"
                            ref={inputRef}
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => choose(e.target.files?.[0])}
                            className="hidden"
                            data-testid="checkin-photo-input"
                        />
                        {preview ? (
                            <div className="relative w-full">
                                <img src={preview} alt="pré-visualização" className="w-full rounded-xl object-cover aspect-[4/3]" />
                                <button
                                    onClick={() => { setFile(null); setPreview(null); }}
                                    className="absolute top-2 right-2 rounded-full bg-black/60 text-white p-1.5"
                                    data-testid="checkin-remove-photo"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => inputRef.current?.click()}
                                data-testid="checkin-choose-photo"
                                className="w-full aspect-[4/3] rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/60 hover:bg-amber-100/60 flex flex-col items-center justify-center text-amber-800"
                            >
                                <Camera className="h-10 w-10 mb-2" />
                                <span className="font-semibold">Tirar foto agora</span>
                                <span className="text-xs text-amber-700 mt-1">{photoRequired ? "Obrigatória nessa tarefa" : "Opcional"}</span>
                            </button>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="checkin-cancel">
                        Cancelar
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={busy || (photoRequired && !file)}
                        className="bg-[#4A7C59] hover:bg-[#3a6549] text-white rounded-full"
                        data-testid="checkin-confirm"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
